// **Betrieb**: Container, Wartung und Neustart, Rollout, Löschen. Jede Aktion
// ist ein Lauf — die Karte erscheint oben, und die Seite scrollt zu ihr.
// Der Rollout wählt einen Build aus der Liste (build-select.tsx); derselbe
// noch einmal heißt „Erneut ausrollen".

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { del, get, post } from "../../api/client";
import { keys } from "../../api/queries";
import type { InstanceContainers, RunResponse } from "../../api/types";
import { errorText } from "../../lib/labels";
import { BUILD_RE } from "../../lib/releases";
import { useNavigate } from "../../router/router";
import { Loading, Note } from "../../ui/badge";
import { Card } from "../../ui/card";
import { ConfirmButton } from "../../ui/confirm-button";
import { Field, Input } from "../../ui/field";
import { BuildSelect, useBuildChoice } from "../build-select";
import { toast } from "../../ui/toaster";
import type { TabProps } from "./tabs";

export function OpsTab({ tenant: t, onRunStarted }: TabProps) {
  const navigate = useNavigate();
  const live = t.state === "active" || t.state === "failed";
  // Der Provisioner kann fehlen — dann eben keine Container-Werte, kein Fehler.
  const inst = useQuery({
    queryKey: keys.tenantInstance(t.id),
    queryFn: async () => { try { return await get<InstanceContainers>(`/tenants/${t.id}/instance`); } catch { return null; } },
  });
  const choice = useBuildChoice(t.build);
  const again = choice.value !== "" && choice.value === t.build;
  const [confirmSlug, setConfirmSlug] = useState("");
  const [deleting, setDeleting] = useState(false);
  const server = inst.data?.server;

  const start = async (path: string, body: Record<string, unknown> = {}) => {
    try {
      const res = await post<RunResponse>(`/tenants/${t.id}/${path}`, body);
      onRunStarted(res.run, { scroll: true });
    } catch (err) { toast(errorText(err), "error"); }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      const res = await del<RunResponse | null>(`/tenants/${t.id}`, { confirm_slug: confirmSlug.trim() });
      if (res?.run) onRunStarted(res.run, { scroll: true, onFinished: () => { toast("Mandant gelöscht"); navigate("/"); } });
      else navigate("/");
    } catch (err) {
      toast(errorText(err), "error");
      setDeleting(false);
    }
  };

  return (
    <>
      <Card title="Container">
        {inst.isPending ? <Loading /> : server ? (
          <>
            <div className="pf-stats">
              <div className="pf-stat"><span className="pf-stat__label">Server</span><span className="pf-stat__value">{server.exists ? (server.running ? "läuft" : "gestoppt") : "fehlt"}</span></div>
              <div className="pf-stat"><span className="pf-stat__label">Gesundheit</span><span className="pf-stat__value">{server.health ?? "—"}</span></div>
            </div>
            <div className="pf-sub mono">{server.image ?? ""}</div>
          </>
        ) : <Note tone="warn">Provisioner nicht erreichbar</Note>}
      </Card>
      <Card
        title="Wartung und Neustart"
        hint="Im Wartungsmodus weist die Instanz neue Prompts mit Begründung ab. Ein Neustart läuft immer im Wartungsfenster (an → warten → Neustart → gesund → aus)."
      >
        <div className="pf-row">
          <button type="button" className="pf-btn" disabled={!live} onClick={() => void start("maintenance", { action: "on" })}>Wartung an</button>
          <button type="button" className="pf-btn" disabled={!live} onClick={() => void start("maintenance", { action: "off" })}>Wartung aus</button>
          <ConfirmButton disabled={!live} confirmLabel="Wirklich neu starten?" onConfirm={() => start("restart")}>Neustart</ConfirmButton>
          {t.state === "active" && <ConfirmButton confirmLabel="Wirklich anhalten?" onConfirm={() => start("suspend")}>Anhalten</ConfirmButton>}
          {t.state === "suspended" && <button type="button" className="pf-btn pf-btn--primary" onClick={() => void start("resume")}>Fortsetzen</button>}
        </div>
      </Card>
      <Card
        title="Rollout"
        hint="Ein Build ist ein vollständiger Satz aus der Pipeline — Server, Chat, Aufgaben, Routinen, Office, Vorlagen. Erst werden alle Images gezogen, dann im Wartungsfenster .env, Migration, Start. Gescheitert: die Wartung bleibt an."
      >
        <div className="pf-row">
          <BuildSelect choice={choice} />
          <ConfirmButton
            className="pf-btn pf-btn--primary" disabled={!live || !choice.value}
            confirmLabel={again ? "Wirklich erneut ausrollen?" : "Wirklich ausrollen?"}
            onConfirm={() => start("rollout", { build: choice.value })}
          >{again ? "Erneut ausrollen" : "Ausrollen"}</ConfirmButton>
        </div>
        {t.build && !BUILD_RE.test(t.build)
          ? <Note tone="warn">Läuft noch auf <code>{t.build}</code> (vor den Builds) — der nächste Rollout bringt den Mandanten auf einen Build.</Note>
          : null}
      </Card>
      <Card
        title="Mandant löschen"
        danger
        hint="Löscht Instanz, Container, Volumes, Netze, Datenbank, Appwrite-Team samt Projekten, Requesty-Key und DNS — endgültig. Sicherungen der Datenbank betreibt Solutec getrennt."
        foot={<button type="button" className="pf-btn pf-btn--danger" disabled={deleting || confirmSlug.trim() !== t.slug} onClick={() => void remove()}>Endgültig löschen</button>}
      >
        <Field label={<>Zum Bestätigen den Slug eintippen: <code>{t.slug}</code></>}>
          <Input name="confirm_slug" autoComplete="off" spellCheck={false} value={confirmSlug} onChange={(e) => setConfirmSlug(e.target.value)} />
        </Field>
      </Card>
    </>
  );
}
